import React from 'react';
import { Helmet } from 'react-helmet';
import {
  generateSEOTags,
  getProductStructuredData,
  getOrganizationStructuredData,
  getBreadcrumbStructuredData,
  getWebPageStructuredData
} from '../../utilities/seo';

/**
 * SEO Component
 * A reusable component for managing SEO meta tags and structured data
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - OG image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - OG type (website, article, product, etc.)
 * @param {string} props.country - Country code
 * @param {string} props.language - Language code (en, id)
 * @param {Array} props.keywords - SEO keywords
 * @param {boolean} props.noindex - Prevent indexing
 * @param {boolean} props.nofollow - Prevent following links
 * @param {Object} props.product - Product data for structured data
 * @param {Object} props.organization - Organization data for structured data
 * @param {Array} props.breadcrumbs - Breadcrumb items for structured data
 * @param {Object} props.webPage - Web page data for structured data
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  country,
  language = 'en',
  keywords = [],
  noindex = false,
  nofollow = false,
  product,
  organization,
  breadcrumbs,
  webPage,
  alternateLanguages = ['en', 'id']
}) => {
  const seoTags = generateSEOTags({
    title,
    description,
    image,
    url,
    type,
    country,
    language,
    keywords,
    noindex,
    nofollow,
    canonical: url,
    alternateLanguages
  });

  // Generate structured data
  const structuredData = [];

  if (product) {
    structuredData.push(getProductStructuredData(product));
  }

  if (organization) {
    structuredData.push(getOrganizationStructuredData(organization));
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    structuredData.push(getBreadcrumbStructuredData(breadcrumbs));
  }

  if (webPage) {
    structuredData.push(getWebPageStructuredData(webPage));
  }

  return (
    <Helmet>
      <title>{seoTags.title}</title>
      {seoTags.meta.map((tag, index) => (
        <meta key={index} {...tag} />
      ))}
      {seoTags.link.map((link, index) => (
        <link key={index} {...link} />
      ))}
      {structuredData.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
